import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Flex,
  Loader,
  Progress,
  Stack,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useReducer, useRef, useState, type CSSProperties } from 'react';
import { IconX } from '@tabler/icons-react';
import {
  isReferralSlotConflictError,
  sendVerificationCode,
  verifyEmailAndRegister,
} from './intakeLeadPatient';
import { confirmHighRiskBooking, isHighRiskBookingConflict } from './highRiskIntakeScheduling';
import { createScreeningQuestionnaireResponse } from './intakeDataService';
import {
  buildIntakeResumeSnapshot,
  INTAKE_SIGNIN_RETURN_PATH,
  saveIntakeResumeSnapshot,
  tryRestoreIntakeResumeSnapshot,
} from './intakeResumeState';
import { applyIntakePatientSession } from './intakePatientSession';
import { getInitialIntakeState, intakeReducer } from './intakeReducer';
import { findIntakeServiceForTherapyType } from './intakeServicesCatalog';
import type { IntakeAnswers, IntakeState, TherapyType } from './types';
import {
  getActiveTherapyTypes,
  getProviderSelectionTherapyTypes,
  getTherapyTypesForSubmission,
  intakeBooksNothing,
  isGroupOnlyIntake,
  isHighRisk,
  needsLocationStep,
  needsScreeningCall,
  TOTAL_STEPS,
} from './types';
import { isStepValid } from './validation';
import { THERAPY_TYPE_OPTIONS } from './therapyTypeOptions';
import { StepTherapyType } from './steps/StepTherapyType';
import { StepTopics } from './steps/StepTopics';
import { StepPreferences } from './steps/StepPreferences';
import { StepScreening } from './steps/StepScreening';
import { StepFormat } from './steps/StepFormat';
import { StepZip } from './steps/StepZip';
import { StepPayment } from './steps/StepPayment';
import { StepCostEstimatesAll } from './steps/StepCostEstimatesAll';
import { StepProviders } from './steps/StepProviders';
import { StepSessionType } from './steps/StepSessionType';
import { StepBooking } from './steps/StepBooking';
import { StepScreeningCall } from './steps/StepScreeningCall';
import { StepHighRiskScreeningBooking } from './steps/StepHighRiskScreeningBooking';
import { StepLocation } from './steps/StepLocation';
import { StepVerifyEmail } from './steps/StepVerifyEmail';
import { StepConfirmation } from './steps/StepConfirmation';
import { IntakeDataProvider, useIntakeDataContext } from './IntakeDataContext';
import { IntakeSessionProvider } from './IntakeSessionContext';
import { useIntakeSession } from './useIntakeSession';
import { useActiveIntakeServices } from './useActiveIntakeServices';
import { intakeAnswersFromPatient } from './intakePatientDemographics';
import {
  countAnsweredPreBookingSteps,
  getDisplayStepNumber,
  getNextLinearIntakeStep,
  getPreBookingStepCount,
  getPreviousLinearIntakeStep,
  GROUP_SELECTION_STEP,
  isRequestServiceFlow,
  needsGroupSelectionStep,
  shouldPinIntakeLeadPatientId,
  type IntakeFlowVariant,
  type IntakeStepShape,
} from './intakeFlowVariant';
import { StepGroupSelection } from './steps/StepGroupSelection';
/** When true, blocks intake after email verification until submission is rebuilt. */
const INTAKE_SUBMISSION_DISABLED = false;
const INTAKE_SUBMISSION_DISABLED_MESSAGE =
  'Intake completion is temporarily unavailable while we update the system.';

function therapyTypeLabel(type: TherapyType): string {
  return THERAPY_TYPE_OPTIONS.find((o) => o.value === type)?.label?.toLowerCase() ?? type;
}

const DEV_MOCK_ANSWERS: IntakeAnswers = {
  therapyTypes: ['individual'],
  cancelledTherapyTypes: [],
  waitlistedTherapyTypes: [],
  skippedAtProviderTherapyTypes: [],
  therapyType: 'individual',
  sessionType: 'intake',
  topics: ['Anxiety', 'Depression'],
  specialties: [],
  demographicPrefs: [],
  language: 'en',
  screeningEmergency: false,
  screeningHospitalization: false,
  screeningSuicidalIdeation: false,
  screeningMedications: false,
  screeningSubstanceUse: false,
  screeningLegalProceedings: false,
  format: 'virtual',
  availabilityTimeOfDay: [],
  availabilityDays: [],
  firstName: 'Jane',
  lastName: 'Doe',
  email: 'jane.doe@example.com',
  dateOfBirth: '1990-01-15',
  zip: '94102',
  payType: 'insurance',
  insuranceCompany: 'Blue Shield of California',
  insurancePayerOrgId: null,
  preferredLocationIds: ['no-preference'],
  emailVerificationCode: '123456',
  selectedProviderId: 'p1',
  bookingDate: dayjs().format('YYYY-MM-DD'),
  bookingTime: '10:00',
  screeningSlotStart: null,
  screeningSlotEnd: null,
  screeningPractitionerId: null,
  memberId: 'MEM123456',
  groupId: 'GRP789',
  insuranceStartDate: '2026-01-01',
  insuranceEndDate: '2026-12-31',
  subscriberRelationship: 'self',
  subscriberFirstName: '',
  subscriberLastName: '',
  subscriberDateOfBirth: '',
  intakeLeadPatientId: null,
  stripeSetupIntentId: null,
  stripePaymentMethodId: null,
  stripePaymentMethodSummary: null,
  cashPaymentArrangedLater: false,
  groupSelection: 'none',
  bookings: [],
};

const STEP_TITLES: Record<number, string> = {
  0: 'What are you looking for today?',
  1: 'What would you like to focus on with your provider?',
  2: 'Any preferences for your provider?',
  3: 'A few questions about your well-being',
  4: 'How would you prefer to meet?',
  5: 'Tell us about yourself',
  6: 'Verify your email',
  7: 'Where would you like to meet in person?',
  8: 'How do you plan to pay for sessions?',
  9: 'Here are your cost estimates',
  10: 'Providers who match your preferences',
  11: 'Choose your session type',
  12: 'Book your intake', // overridden dynamically by sessionType
  13: 'Review your intake details',
  [GROUP_SELECTION_STEP]: 'Choose a Group',
};

const LOADING_MESSAGES = [
  'Searching for providers',
  'Checking availability',
  'Personalizing results',
];

/** Group-only requests match no providers, so the provider-search copy would be misleading. */
const PENDING_REQUEST_LOADING_MESSAGES = [
  'Preparing your request',
  'Checking group availability',
  'Almost there',
];

const LOADING_MESSAGE_DISPLAY_MS = 2000;
const LOADING_DURATION_MIN_MS = 3000;
const LOADING_DURATION_MAX_MS = 10000;

function randomLoadingDurationMs(): number {
  return (
    Math.floor(Math.random() * (LOADING_DURATION_MAX_MS - LOADING_DURATION_MIN_MS + 1)) +
    LOADING_DURATION_MIN_MS
  );
}

function renderStepContent(
  step: number,
  state: IntakeState,
  dispatch: React.Dispatch<import('./types').IntakeAction>,
  opts?: {
    isCompleting?: boolean;
    redirectSecondsRemaining?: number;
    billingPatientId?: string;
    isScreeningCallStep?: boolean;
    isHighRiskScreeningStep?: boolean;
    verificationError?: string | null;
    resendLoading?: boolean;
    onResend?: () => void | Promise<void>;
    onWaitlist?: () => void;
    onDecline?: () => void;
    intakeServices?: import('./intakeServicesCatalog').IntakeServiceCatalogEntry[];
    intakeServicesLoading?: boolean;
    intakeServicesError?: string | null;
    onToggleBookingCancelled?: (
      index: number,
      cancelled: boolean,
      booking: import('./types').TherapyBooking,
    ) => void;
    excludedTherapyTypes?: TherapyType[];
    completionCloseLabel?: string;
    organizationId?: string;
  }
) {
  const props = { answers: state.answers, dispatch };
  if (opts?.isHighRiskScreeningStep && step === 12) {
    return <StepHighRiskScreeningBooking {...props} />;
  }
  if (opts?.isScreeningCallStep && step === 12) {
    return <StepScreeningCall {...props} />;
  }
  switch (step) {
    case 0:
      return (
        <StepTherapyType
          {...props}
          services={opts?.intakeServices ?? []}
          servicesLoading={opts?.intakeServicesLoading ?? false}
          servicesError={opts?.intakeServicesError ?? null}
          excludedTherapyTypes={opts?.excludedTherapyTypes}
        />
      );
    case GROUP_SELECTION_STEP:
      return <StepGroupSelection {...props} organizationId={opts?.organizationId} />;
    case 1:
      return <StepTopics {...props} />;
    case 2:
      return <StepPreferences {...props} />;
    case 3:
      return <StepScreening {...props} />;
    case 4:
      return <StepFormat {...props} />;
    case 5:
      return <StepZip {...props} />;
    case 6:
      return (
        <StepVerifyEmail
          {...props}
          verificationError={opts?.verificationError}
          resendLoading={opts?.resendLoading}
          onResend={opts?.onResend}
        />
      );
    case 7:
      return <StepLocation {...props} />;
    case 8:
      return <StepPayment {...props} />;
    case 9:
      return (
        <StepCostEstimatesAll
          answers={state.answers}
          dispatch={dispatch}
          services={opts?.intakeServices ?? []}
        />
      );
    case 10:
      return (
        <StepProviders
          answers={state.answers}
          dispatch={dispatch}
          onWaitlist={opts?.onWaitlist}
          onDecline={opts?.onDecline}
        />
      );
    case 11: {
      const roundTypes = getProviderSelectionTherapyTypes(state.answers);
      const roundType = roundTypes[state.currentBookingRound];
      const serviceCatalogEntry = roundType
        ? findIntakeServiceForTherapyType(opts?.intakeServices ?? [], roundType)
        : null;
      return <StepSessionType {...props} serviceCatalogEntry={serviceCatalogEntry} />;
    }
    case 12:
      return <StepBooking {...props} />;
    case 13:
      return (
        <StepConfirmation
          {...props}
          isCompleting={opts?.isCompleting}
          redirectSecondsRemaining={opts?.redirectSecondsRemaining}
          onToggleBookingCancelled={opts?.onToggleBookingCancelled}
          services={opts?.intakeServices ?? []}
          completionCloseLabel={opts?.completionCloseLabel}
        />
      );
    default:
      return null;
  }
}

export interface IntakeFlowProps {
  initialTherapyType?: TherapyType;
  /** Called when user closes (X) or completes "Confirm booking" */
  onClose?: () => void;
  /** Show close (X) button in header; set false for inline page */
  showCloseButton?: boolean;
  /** Optional: parent can use for layout (e.g. modal width on steps 5/6) */
  onStepChange?: (step: number) => void;
  /** Optional: parent can react to completion state (e.g. shrink modal) */
  onCompletingChange?: (isCompleting: boolean) => void;
  /** Client portal: patient id for Stripe SetupIntent (same as billing page). */
  billingPatientId?: string;
  /** Practice-specific intake link token from /intake/:intakeToken route. When present, scopes intake to that practice. */
  intakeLinkToken?: string;
  /** Organization id from practice intake link query (?org=). Avoids client-side Organization search. */
  intakeOrganizationId?: string;
  /** When true, flow is rendered inside a Mantine modal (compact layout, no outer card chrome). */
  inModal?: boolean;
  /**
   * `request-service` — existing portal client requesting an additional service; skips screening,
   * demographics, and email verification.
   */
  flowVariant?: IntakeFlowVariant;
  /** Hide therapy types the client already has active (request-service). */
  excludedTherapyTypes?: TherapyType[];
  /** Called when the post-submit summary finishes instead of redirecting to /signin. */
  onComplete?: () => void;
  /** Summary screen message when `onComplete` is used (default: redirecting home). */
  completionCloseLabel?: string;
  // Client creation path 4: family/couples/guarantor participants on EpisodeOfCarePage (manage-eoc-party bot).
}

export function IntakeFlow(props: IntakeFlowProps): JSX.Element {
  const intakeOrganizationId = props.intakeOrganizationId;
  return (
    <IntakeDataProvider
      intakeLinkToken={props.intakeLinkToken}
      intakeOrganizationId={intakeOrganizationId}
    >
      <IntakeFlowInner {...props} intakeOrganizationId={intakeOrganizationId} />
    </IntakeDataProvider>
  );
}

function IntakeFlowInner({
  initialTherapyType,
  onClose,
  showCloseButton = true,
  onStepChange,
  onCompletingChange,
  billingPatientId,
  intakeLinkToken,
  intakeOrganizationId: intakeOrganizationIdFromLink,
  inModal = false,
  flowVariant = 'full',
  excludedTherapyTypes = [],
  onComplete,
  completionCloseLabel,
}: IntakeFlowProps): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const {
    submit,
    isMock,
    providerTaskMap,
    refreshProviders,
    authReady,
    authError,
    intakeOrganizationId,
  } = useIntakeDataContext();
  const {
    services: intakeServices,
    loading: intakeServicesLoading,
    error: intakeServicesError,
  } = useActiveIntakeServices(authReady);
  const [state, dispatch] = useReducer(
    intakeReducer,
    undefined,
    () =>
      getInitialIntakeState({
        ...(initialTherapyType ? { therapyType: initialTherapyType } : {}),
        ...(billingPatientId ? { intakeLeadPatientId: billingPatientId } : {}),
      })
  );

  const [isLoadingProviders, setIsLoadingProviders] = useState(false);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [loadingMessageVisible, setLoadingMessageVisible] = useState(true);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isScreeningCallPhase, setIsScreeningCallPhase] = useState(false);
  const [isTransitionLoading, setIsTransitionLoading] = useState(false);
  const [pendingTransitionStep, setPendingTransitionStep] = useState<number | null>(null);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [redirectSecondsRemaining, setRedirectSecondsRemaining] = useState<number>(10);
  const [stepActionLoading, setStepActionLoading] = useState(false);
  const [intakeSubmissionBlocked, setIntakeSubmissionBlocked] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [highRiskBookingConflict, setHighRiskBookingConflict] = useState(false);
  const intakeDraftInitializedRef = useRef(false);
  const stepActionInFlightRef = useRef(false);
  const intakeResumeCheckedRef = useRef(false);
  const patientPrefilledRef = useRef(false);

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const { activeStep, answers, currentBookingRound } = state;
  const referralTaskId = answers.selectedProviderId
    ? providerTaskMap[answers.selectedProviderId] ?? null
    : null;
  const intakeSession = useIntakeSession({
    medplum,
    patientId: answers.intakeLeadPatientId,
    referralTaskId,
    enabled: !isMock,
    organizationId: intakeOrganizationId ?? intakeOrganizationIdFromLink ?? undefined,
  });
  // Timers read the latest answers/session without re-subscribing (both change identity per render).
  const answersRef = useRef(answers);
  const intakeSessionRef = useRef(intakeSession);
  useEffect(() => {
    answersRef.current = answers;
    intakeSessionRef.current = intakeSession;
  });
  const clientIsHighRisk = isHighRisk(answers);
  // High-risk and IOP book a screening call here; Group's is arranged from the tracker instead.
  const clientNeedsScreening = needsScreeningCall(answers);
  const includeGroupSelection = needsGroupSelectionStep(answers.therapyTypes);
  const groupOnly = isGroupOnlyIntake(answers);
  const activeTherapyTypes = getProviderSelectionTherapyTypes(answers);
  const numRounds = Math.max(activeTherapyTypes.length, 1);

  // Clinical-session HS ids a provider must be entitled to deliver for a given round's service,
  // derived from that Episode-of-Care type's intakeType (passed to the matching bot's service-type filter).
  const requiredHsIdsForRound = useCallback(
    (round: number): string[] => {
      const type = activeTherapyTypes[round];
      if (!type) return [];
      const entry = findIntakeServiceForTherapyType(intakeServices, type);
      if (!entry) return [];
      const intakeId = entry.sessions.intake?.id;
      const consultId = entry.sessions.consultation?.id;
      const ids: string[] = [];
      if (entry.intakeType === 'consultations') {
        if (consultId) ids.push(consultId);
      } else if (entry.intakeType === 'intake-sessions') {
        if (intakeId) ids.push(intakeId);
      } else {
        if (intakeId) ids.push(intakeId);
        if (consultId) ids.push(consultId);
      }
      return ids;
    },
    [activeTherapyTypes, intakeServices],
  );
  const lastMatchedKeyRef = useRef<string>('');
  const matchKeyForRound = useCallback(
    (round: number): string => `${round}:${requiredHsIdsForRound(round).join(',')}`,
    [requiredHsIdsForRound],
  );
  const validationCtx = useMemo(
    () => ({
      billingPatientId,
      intakeLeadPatientId: answers.intakeLeadPatientId,
      profileResourceType: profile?.resourceType,
      currentRoundTherapyType: activeTherapyTypes[currentBookingRound],
    }),
    [
      billingPatientId,
      answers.intakeLeadPatientId,
      profile?.resourceType,
      activeTherapyTypes,
      currentBookingRound,
    ],
  );
  // Both cash and insurance show the cost-estimate screen (step 9) once before the per-round
  // provider/session/booking loop (steps 10-12). The screen lists each selected service with its
  // own cost + a cancel option, so cancellations happen up front and the round indices stay stable.
  const stepsPerRound = 4;
  // Group-only takes its modality and location from the cohort, so neither step is asked.
  const hasLocationStep = !groupOnly && needsLocationStep(answers.format);
  const skipEmailVerification = Boolean(answers.intakeLeadPatientId);
  const requestServiceFlow = isRequestServiceFlow(flowVariant);
  const stepShape: IntakeStepShape = useMemo(
    () => ({
      hasLocationStep,
      includeGroupSelection,
      skipMatchingPreferenceSteps: groupOnly,
    }),
    [hasLocationStep, includeGroupSelection, groupOnly],
  );

  useEffect(() => {
    if (!requestServiceFlow || !billingPatientId || patientPrefilledRef.current) return;
    let cancelled = false;
    void medplum
      .readResource('Patient', billingPatientId)
      .then((patient) => {
        if (cancelled) return;
        patientPrefilledRef.current = true;
        const demographics = intakeAnswersFromPatient(patient);
        if (demographics.firstName) {
          dispatch({ type: 'SET_FIRST_NAME', payload: demographics.firstName });
        }
        if (demographics.lastName) {
          dispatch({ type: 'SET_LAST_NAME', payload: demographics.lastName });
        }
        if (demographics.email) {
          dispatch({ type: 'SET_EMAIL', payload: demographics.email });
        }
        if (demographics.dateOfBirth) {
          dispatch({ type: 'SET_DATE_OF_BIRTH', payload: demographics.dateOfBirth });
        }
        if (demographics.zip) {
          dispatch({ type: 'SET_ZIP', payload: demographics.zip });
        }
        if (demographics.emailVerified) {
          dispatch({ type: 'SET_EMAIL_VERIFIED', payload: true });
        }
      })
      .catch((e) => {
        console.warn('[Intake] could not prefill patient demographics', e);
      });
    return () => {
      cancelled = true;
    };
  }, [requestServiceFlow, billingPatientId, medplum]);

  useEffect(() => {
    if (!shouldPinIntakeLeadPatientId(flowVariant, billingPatientId, answers.intakeLeadPatientId)) {
      return;
    }
    dispatch({ type: 'SET_INTAKE_LEAD_PATIENT_ID', payload: billingPatientId as string });
  }, [flowVariant, billingPatientId, answers.intakeLeadPatientId]);

  useEffect(() => {
    if (excludedTherapyTypes.length === 0) return;
    const excluded = new Set(excludedTherapyTypes);
    const nextTypes = answers.therapyTypes.filter((t) => !excluded.has(t));
    if (nextTypes.length !== answers.therapyTypes.length) {
      dispatch({ type: 'SET_THERAPY_TYPES', payload: nextTypes });
    }
  }, [excludedTherapyTypes, answers.therapyTypes, dispatch]);

  useEffect(() => {
    if (intakeResumeCheckedRef.current || isMock) return;
    if (profile?.resourceType !== 'Patient' || !profile.id) return;
    intakeResumeCheckedRef.current = true;
    tryRestoreIntakeResumeSnapshot(dispatch, { resumePatientId: profile.id });
  }, [profile, isMock]);

  // Screening-only flows (Group cohort, IOP, high-risk) skip the provider loop entirely, so their
  // total counts the screening-call screen rather than a booking round that never runs.
  const bookingRoundCount = activeTherapyTypes.length;
  const effectiveTotal =
    getPreBookingStepCount(flowVariant, stepShape) +
    1 +
    bookingRoundCount * stepsPerRound +
    (bookingRoundCount === 0 && clientNeedsScreening ? 1 : 0) +
    1;
  const canNext = isStepValid(activeStep, answers, validationCtx);
  const isFinalStep = activeStep === TOTAL_STEPS - 1;
  const isLastRound = currentBookingRound >= numRounds - 1;
  const firstRoundStep = 9;
  const allServicesCancelledOnBenefits =
    activeStep === 9 && getActiveTherapyTypes(answers).length === 0;
  const allBookingsCancelledOnReview =
    activeStep === 13 && answers.bookings.length > 0 && !answers.bookings.some((b) => !b.cancelled);
  const showCancelIntakeButton = allServicesCancelledOnBenefits || allBookingsCancelledOnReview;

  const confirmButtonLabel = (() => {
    const active = answers.bookings.filter((b) => !b.cancelled);
    const isWaitlistedBooking = (b: (typeof active)[number]) =>
      !b.isScreeningCall &&
      (b.waitlisted || (answers.waitlistedTherapyTypes ?? []).includes(b.therapyType));
    const waitlisted = active.filter(isWaitlistedBooking);
    const scheduled = active.filter((b) => !b.isScreeningCall && !isWaitlistedBooking(b));
    if (active.length === 0) return 'Submit request';
    if (waitlisted.length > 0 && scheduled.length === 0) {
      return waitlisted.length > 1 ? 'Submit requests' : 'Submit request';
    }
    return numRounds > 1 ? 'Confirm bookings' : 'Confirm booking';
  })();

  const answeredCount = (() => {
    const isStepAnswered = (i: number) => isStepValid(i, answers, validationCtx);
    let n = countAnsweredPreBookingSteps(activeStep, flowVariant, stepShape, isStepAnswered);
    if (bookingRoundCount === 0) {
      // Screening-only: the screening call is the single screen between loading and review.
      if (clientNeedsScreening && activeStep >= 9) n += 1;
    } else {
      n += answers.bookings.length * stepsPerRound;
      if (activeStep >= 9 && activeStep <= 12) {
        for (let i = firstRoundStep; i <= activeStep; i++) {
          if (isStepAnswered(i)) n += 1;
        }
      }
    }
    if (activeStep === 13) n += 1;
    return n;
  })();
  const progress = (answeredCount / effectiveTotal) * 100;

  // Drafts are normally created on entry to provider selection. A flow that books nothing never
  // gets there, so it creates them during the loading screen instead — without a draft, submit
  // falls back to a path that drops the requested cohort and its tracker stage. High-risk is
  // excluded because it submits through confirmHighRiskBooking and never promotes.
  const shouldCreateDraftResources =
    activeStep === 10 || (isLoadingProviders && !clientIsHighRisk && intakeBooksNothing(answers));

  useEffect(() => {
    if (isMock || !shouldCreateDraftResources || !answers.intakeLeadPatientId || intakeDraftInitializedRef.current) {
      return;
    }
    intakeDraftInitializedRef.current = true;
    void intakeSession.createDraftResources(answers).catch((e) => {
      console.warn('[Intake] create draft resources failed', e);
      intakeDraftInitializedRef.current = false;
    });
  }, [shouldCreateDraftResources, answers, answers.intakeLeadPatientId, intakeSession, isMock]);

  // Re-match providers for each booking round with that round's service-type filter. Round 0 is
  // matched at step 8 (during the loading screen); rounds 1+ re-match on entry to the provider step.
  useEffect(() => {
    if (isMock || !authReady || activeStep !== 10) return;
    const key = matchKeyForRound(currentBookingRound);
    if (lastMatchedKeyRef.current === key) return;
    lastMatchedKeyRef.current = key;
    void refreshProviders(answers, requiredHsIdsForRound(currentBookingRound)).catch((e) => {
      console.warn('[Intake] per-round provider match failed', e);
    });
  }, [
    activeStep,
    currentBookingRound,
    isMock,
    authReady,
    refreshProviders,
    requiredHsIdsForRound,
    matchKeyForRound,
    answers,
  ]);

  const handleSendVerificationCode = useCallback(async () => {
    if (!authReady) {
      notifications.show({
        color: 'red',
        title: 'Intake is not ready',
        message: authError ?? 'Still connecting to the intake service. Please try again in a moment.',
      });
      return false;
    }
    setStepActionLoading(true);
    try {
      await sendVerificationCode(medplum, { email: answers.email.trim() });
      setVerificationError(null);
      return true;
    } catch (e) {
      console.error('[Intake] Verification code send failed:', e);
      notifications.show({
        color: 'red',
        title: 'Could not send verification code',
        message:
          e instanceof Error
            ? e.message
            : 'We could not send a code to your email. Please try again.',
      });
      return false;
    } finally {
      setStepActionLoading(false);
    }
  }, [answers.email, authError, authReady, medplum]);

  const submitHighRiskBooking = useCallback(async () => {
    const start = answers.screeningSlotStart;
    const end = answers.screeningSlotEnd;
    const practitionerId = answers.screeningPractitionerId;
    if (!start || !end || !practitionerId) {
      throw new Error('Please choose an intake call time.');
    }
    setHighRiskBookingConflict(false);
    const result = await confirmHighRiskBooking(medplum, {
      start,
      end,
      practitionerId,
      therapyTypeCodes: getTherapyTypesForSubmission(answers),
      payType: answers.payType,
      organizationId: intakeOrganizationId ?? intakeOrganizationIdFromLink ?? undefined,
    });
    // Persist the six screening answers as a QuestionnaireResponse so staff can review them on the
    // tracker. Only on a fresh booking — `alreadyBooked` means a prior run already created it.
    const patientId = answers.intakeLeadPatientId?.trim();
    if (!result.alreadyBooked && patientId) {
      await createScreeningQuestionnaireResponse(medplum, { patientId, answers });
    }
  }, [answers, medplum, intakeOrganizationId, intakeOrganizationIdFromLink]);

  const handleWaitlistOptIn = useCallback(() => {
    const therapyType = activeTherapyTypes[currentBookingRound];
    if (!therapyType) return;
    if ((answers.skippedAtProviderTherapyTypes ?? []).includes(therapyType)) {
      dispatch({
        type: 'SET_SKIPPED_AT_PROVIDER',
        payload: { therapyType, skipped: false },
      });
    }
    dispatch({
      type: 'SET_THERAPY_TYPE_WAITLISTED',
      payload: { therapyType, waitlisted: true },
    });
    dispatch({ type: 'SAVE_CURRENT_BOOKING' });
    const nextRound = currentBookingRound + 1;
    if (nextRound < numRounds) {
      dispatch({ type: 'SET_CURRENT_BOOKING_ROUND', payload: nextRound });
      setIsTransitionLoading(true);
      setPendingTransitionStep(10);
    } else {
      dispatch({ type: 'SET_STEP', payload: 13 });
    }
  }, [activeTherapyTypes, answers, currentBookingRound, numRounds]);

  const handleWaitlistDecline = useCallback(() => {
    const skippedType = activeTherapyTypes[currentBookingRound];
    if (!skippedType) return;
    dispatch({
      type: 'SET_SKIPPED_AT_PROVIDER',
      payload: { therapyType: skippedType, skipped: true },
    });
    const tempAnswers: IntakeAnswers = {
      ...answers,
      skippedAtProviderTherapyTypes: [...(answers.skippedAtProviderTherapyTypes ?? []), skippedType],
    };
    if (getProviderSelectionTherapyTypes(tempAnswers).length === 0) {
      dispatch({ type: 'SET_STEP', payload: 13 });
    } else {
      setIsTransitionLoading(true);
      setPendingTransitionStep(10);
    }
  }, [activeTherapyTypes, answers, currentBookingRound]);

  const handleNext = useCallback(async () => {
    if (stepActionInFlightRef.current) return;

    if (activeStep === 5) {
      if (!isMock && !answers.intakeLeadPatientId) {
        stepActionInFlightRef.current = true;
        try {
          const sent = await handleSendVerificationCode();
          if (!sent) return;
        } finally {
          stepActionInFlightRef.current = false;
        }
      }
      dispatch({ type: 'SET_STEP', payload: 6 });
      return;
    }
    if (activeStep === 6) {
      if (!isMock && !answers.intakeLeadPatientId) {
        if (!authReady) {
          notifications.show({
            color: 'red',
            title: 'Intake is not ready',
            message: authError ?? 'Still connecting to the intake service. Please try again in a moment.',
          });
          return;
        }
        stepActionInFlightRef.current = true;
        setStepActionLoading(true);
        setVerificationError(null);
        try {
          const result = await verifyEmailAndRegister(medplum, {
            email: answers.email.trim(),
            code: answers.emailVerificationCode,
            firstName: answers.firstName.trim(),
            lastName: answers.lastName.trim(),
            dateOfBirth: answers.dateOfBirth,
            zip: answers.zip,
            organizationId: intakeOrganizationIdFromLink ?? intakeOrganizationId ?? undefined,
            intakeLinkToken,
            isHighRisk: clientIsHighRisk,
          });
          if (result.status === 'existing-client') {
            saveIntakeResumeSnapshot(buildIntakeResumeSnapshot(answers, result.patientId));
            dispatch({ type: 'SET_EXISTING_CLIENT', payload: true });
            window.location.assign(INTAKE_SIGNIN_RETURN_PATH);
            setStepActionLoading(false);
            return;
          }

          // The bot minted the Patient session server-side (no transmitted password).
          // Install it to replace the anonymous client_credentials session.
          try {
            await applyIntakePatientSession(medplum, result.login);
          } catch (sessionErr) {
            console.error('[Intake] applying minted Patient session failed:', sessionErr);
            // The account is already safe (nothing reusable was transmitted); recover via sign-in.
            saveIntakeResumeSnapshot(buildIntakeResumeSnapshot(answers, result.patientId));
            setVerificationError(
              'Your account is ready but we could not start your secure session. Please sign in to continue intake.',
            );
            window.location.assign(INTAKE_SIGNIN_RETURN_PATH);
            setStepActionLoading(false);
            return;
          }

          dispatch({ type: 'SET_INTAKE_LEAD_PATIENT_ID', payload: result.patientId });
          dispatch({ type: 'SET_EMAIL_VERIFIED', payload: true });
        } catch (e) {
          console.error('[Intake] Email verification failed:', e);
          setVerificationError(
            e instanceof Error ? e.message : 'Incorrect or expired verification code. Please try again.',
          );
          setStepActionLoading(false);
          return;
        } finally {
          stepActionInFlightRef.current = false;
        }
        setStepActionLoading(false);
      }
      if (INTAKE_SUBMISSION_DISABLED) {
        setIntakeSubmissionBlocked(true);
        return;
      }
      dispatch({ type: 'SET_STEP', payload: hasLocationStep ? 7 : 8 });
      return;
    }
    if (activeStep === 8) {
      if (!isMock && authReady) {
        // Match for the first booking round's service (service-type filtered). Subsequent rounds
        // re-match via the step-10 effect as currentBookingRound advances.
        void refreshProviders(answers, requiredHsIdsForRound(currentBookingRound));
        lastMatchedKeyRef.current = matchKeyForRound(currentBookingRound);
      }
      setIsLoadingProviders(true);
      setLoadingMessageIndex(0);
      setLoadingMessageVisible(true);
      return;
    }
    if (activeStep === 12) {
      if (isScreeningCallPhase) {
        dispatch({ type: 'SAVE_SCREENING_CALL_BOOKING' });
        setIsScreeningCallPhase(false);
        if (clientIsHighRisk) {
          dispatch({ type: 'SET_STEP', payload: 13 });
        } else if (activeTherapyTypes.length > 0) {
          dispatch({ type: 'SET_CURRENT_BOOKING_ROUND', payload: 0 });
          setIsTransitionLoading(true);
          setPendingTransitionStep(10);
        } else {
          dispatch({ type: 'SET_STEP', payload: 13 });
        }
        return;
      }
      // When editing a prior round (reached via POP_LAST_BOOKING), the live hook id may be null/stale,
      // so fall back to the round's saved reservation appointment id.
      const proposedAppointmentId =
        intakeSession.proposedAppointmentId ?? answers.currentReservation?.appointmentId ?? null;
      if (!isMock && proposedAppointmentId && answers.bookingDate && answers.bookingTime) {
        try {
          const dur = answers.sessionType === 'consultation' ? 15 : 50;
          await intakeSession.patchProposedAppointmentTime(
            proposedAppointmentId,
            answers.bookingDate,
            answers.bookingTime,
            dur,
          );
        } catch (e) {
          if (isReferralSlotConflictError(e)) {
            // The calendar Slot was taken between selecting the time and confirming it. Keep the
            // referral reservation, clear the chosen time, and make the client pick a new one.
            notifications.show({
              color: 'yellow',
              title: 'That time was just taken',
              message: 'Someone else just booked that time. Please choose another available time.',
            });
            dispatch({ type: 'SET_BOOKING_TIME', payload: null });
            return;
          }
          console.warn('[Intake] patch proposed appointment failed', e);
        }
      }
      dispatch({ type: 'SAVE_CURRENT_BOOKING' });
      const nextRound = currentBookingRound + 1;
      if (nextRound < numRounds) {
        dispatch({ type: 'SET_CURRENT_BOOKING_ROUND', payload: nextRound });
        setIsTransitionLoading(true);
        setPendingTransitionStep(10);
      } else {
        dispatch({ type: 'SET_STEP', payload: 13 });
      }
      return;
    }
    if (activeStep === 10) {
      const currentType = activeTherapyTypes[currentBookingRound];
      if (currentType && (answers.waitlistedTherapyTypes ?? []).includes(currentType)) {
        const nextRound = currentBookingRound + 1;
        if (nextRound < numRounds) {
          dispatch({ type: 'SET_CURRENT_BOOKING_ROUND', payload: nextRound });
          setIsTransitionLoading(true);
          setPendingTransitionStep(10);
        } else {
          dispatch({ type: 'SET_STEP', payload: 13 });
        }
        return;
      }
      // Mock mode advances without a hold.
      if (isMock) {
        dispatch({ type: 'SET_STEP', payload: 11 });
        return;
      }
      // A provider is selected, so reserve the referral slot now — before the client picks a
      // session type or date/time. The Step 10 Next button stays disabled until drafts are ready,
      // so these guards only fire in unexpected states. Never advance without a confirmed hold.
      if (!answers.selectedProviderId || !intakeSession.draftReady || !referralTaskId) {
        notifications.show({
          color: 'red',
          title: 'Could not reserve provider',
          message:
            'We could not prepare your booking. Please refresh the provider list or choose another provider.',
        });
        return;
      }
      stepActionInFlightRef.current = true;
      setStepActionLoading(true);
      try {
        // Defensive: if a stale hold from a prior round survived (e.g. a path that reached step 10
        // without traversing 11 -> 10), release it before reserving a new provider so we never
        // create a second hold for the same episode. A failure here throws and blocks the reserve.
        if (answers.currentReservation) {
          await intakeSession.releaseReservationForBooking(answers.currentReservation);
          dispatch({ type: 'SET_CURRENT_RESERVATION', payload: null });
        }
        const reservation = await intakeSession.reserveForBookingRound(
          answers,
          currentBookingRound,
        );
        if (!reservation) {
          notifications.show({
            color: 'red',
            title: 'Could not reserve provider',
            message: 'Please try again or choose a different provider.',
          });
          return;
        }
        dispatch({ type: 'SET_CURRENT_RESERVATION', payload: reservation });
        dispatch({ type: 'SET_STEP', payload: 11 });
      } catch (e) {
        if (isReferralSlotConflictError(e)) {
          notifications.show({
            color: 'yellow',
            title: 'Slot just taken',
            message:
              "This provider's slot was just taken. Pick another provider or try again — we've kept your session active.",
          });
        } else {
          console.error('[Intake] reserve referral slot failed', e);
          notifications.show({
            color: 'red',
            title: 'Could not reserve provider',
            message: 'Please try again or choose a different provider.',
          });
        }
      } finally {
        stepActionInFlightRef.current = false;
        setStepActionLoading(false);
      }
      return;
    }
    dispatch({
      type: 'SET_STEP',
      payload: getNextLinearIntakeStep(activeStep, flowVariant, stepShape),
    });
  }, [
    activeStep,
    activeTherapyTypes,
    answers,
    clientIsHighRisk,
    currentBookingRound,
    flowVariant,
    hasLocationStep,
    stepShape,
    intakeSession,
    isMock,
    isScreeningCallPhase,
    medplum,
    numRounds,
    referralTaskId,
    authReady,
    authError,
    refreshProviders,
    requiredHsIdsForRound,
    matchKeyForRound,
    handleSendVerificationCode,
    intakeOrganizationId,
    intakeLinkToken,
    intakeOrganizationIdFromLink,
    hasLocationStep,
  ]);

  useEffect(() => {
    if (!isLoadingProviders) return;
    const durationMs = randomLoadingDurationMs();
    const messageCycleInterval = setInterval(() => {
      setLoadingMessageVisible((visible) => {
        if (visible) return false;
        setLoadingMessageIndex((i) => (i + 1) % LOADING_MESSAGES.length);
        return true;
      });
    }, LOADING_MESSAGE_DISPLAY_MS);
    let cancelled = false;
    const doneTimeout = setTimeout(() => {
      void (async () => {
        // Group-only (and other book-nothing paths) must finish draft SR/EoC before review —
        // otherwise submit falls through to a stub that always throws. Read through refs: the
        // session object and answers change identity every render, and depending on them here
        // would restart this timer forever.
        const session = intakeSessionRef.current;
        const currentAnswers = answersRef.current;
        if (
          !cancelled &&
          !isMock &&
          !clientNeedsScreening &&
          activeTherapyTypes.length === 0 &&
          currentAnswers.intakeLeadPatientId &&
          !session.draftReady
        ) {
          try {
            await session.createDraftResources(currentAnswers);
          } catch (e) {
            console.warn('[Intake] create draft resources failed before review', e);
          }
        }
        if (cancelled) return;
        setIsLoadingProviders(false);
        if (clientNeedsScreening) {
          // High-risk and IOP book their screening call here.
          setIsScreeningCallPhase(true);
          dispatch({ type: 'SET_STEP', payload: 12 });
        } else if (activeTherapyTypes.length === 0) {
          // Group-only: no provider rounds and no in-flow screening — straight to review, where it
          // submits as a pending request for staff to schedule from the tracker.
          dispatch({ type: 'SET_STEP', payload: 13 });
        } else {
          // Non-screening cash/insurance lands on the cost-estimate screen (step 9) before the loop.
          dispatch({ type: 'SET_STEP', payload: 9 });
        }
      })();
    }, durationMs);
    return () => {
      cancelled = true;
      clearInterval(messageCycleInterval);
      clearTimeout(doneTimeout);
    };
  }, [isLoadingProviders, clientNeedsScreening, activeTherapyTypes.length, isMock]);

  useEffect(() => {
    if (!isTransitionLoading || pendingTransitionStep == null) return;
    const timeout = setTimeout(() => {
      setIsTransitionLoading(false);
      dispatch({ type: 'SET_STEP', payload: pendingTransitionStep });
      setPendingTransitionStep(null);
    }, 1000);
    return () => clearTimeout(timeout);
  }, [isTransitionLoading, pendingTransitionStep]);

  useEffect(() => {
    // If user navigates away from final step, reset completion state.
    if (!isFinalStep) {
      setIsCompleting(false);
      setIsFadingOut(false);
      setRedirectSecondsRemaining(10);
    }
  }, [isFinalStep]);

  useEffect(() => {
    if (!isCompleting) return;

    const totalSeconds = 10;
    const fadeMs = 900;
    setRedirectSecondsRemaining(totalSeconds);

    const tickInterval = window.setInterval(() => {
      setRedirectSecondsRemaining((s) => Math.max(0, s - 1));
    }, 1000);

    const fadeTimeout = window.setTimeout(() => setIsFadingOut(true), totalSeconds * 1000 - fadeMs);
    const redirectTimeout = window.setTimeout(() => {
      if (onComplete) {
        onComplete();
      } else {
        window.location.assign('/signin');
      }
    }, totalSeconds * 1000);

    return () => {
      window.clearInterval(tickInterval);
      window.clearTimeout(fadeTimeout);
      window.clearTimeout(redirectTimeout);
    };
  }, [isCompleting, onComplete]);

  const showingLoading = isLoadingProviders || isTransitionLoading;
  const loadingMessages = bookingRoundCount === 0 && !clientNeedsScreening
    ? PENDING_REQUEST_LOADING_MESSAGES
    : LOADING_MESSAGES;
  const preBookingCount = getPreBookingStepCount(flowVariant, stepShape);
  const displayStepNum = getDisplayStepNumber(activeStep, flowVariant, {
    ...stepShape,
    showingLoading,
    preBookingCount,
    currentBookingRound,
    stepsPerRound,
    firstRoundStep,
    effectiveTotal,
    bookingRoundCount,
  });
  const isWideStep = activeStep === 10;
  const stepEnterTransitionMs = activeStep >= 10 ? 700 : 220;
  const stepEnterStyle = { '--intake-step-enter-ms': `${stepEnterTransitionMs}ms` } as CSSProperties;

  useEffect(() => {
    onStepChange?.(activeStep);
  }, [activeStep, onStepChange]);

  useEffect(() => {
    onCompletingChange?.(isCompleting);
  }, [isCompleting, onCompletingChange]);

  const completingStyle: CSSProperties | undefined = isCompleting
    ? {
      opacity: isFadingOut ? 0 : 1,
      transition: 'opacity 900ms ease',
      pointerEvents: 'none',
    }
    : undefined;

  return (
    <IntakeSessionProvider value={intakeSession}>
      <Box
        className={inModal ? 'intake-flow intake-flow--in-modal' : 'intake-flow'}
        data-wide={isWideStep || undefined}
        data-completing={isCompleting || undefined}
        style={completingStyle}
      >
        {!isCompleting && (
          <Flex className="intake-flow-header" justify="space-between" align="center">
            <Text size="sm" fw={600} c="dimmed">
              {`Step ${displayStepNum} of ${effectiveTotal}`}
            </Text>
            {showCloseButton && onClose && (
              <ActionIcon variant="subtle" size="lg" onClick={handleClose} aria-label="Close">
                <IconX size={20} />
              </ActionIcon>
            )}
          </Flex>
        )}

        {!isCompleting && !isMock && intakeSession.showExpiryWarning && !intakeSession.expired && (
          <Alert color="yellow" variant="light" radius="md" title="Session ending soon" mx="md" mt="xs">
            Your intake reservation expires in {intakeSession.secondsUntilExpiry ?? 0}s. Interact with the form
            to extend it.
          </Alert>
        )}
        {!isCompleting && !isMock && intakeSession.expired && (
          <Alert color="red" variant="light" radius="md" title="Session expired" mx="md" mt="xs">
            Your reservation timed out. Close this intake and start again.
          </Alert>
        )}
        {!isCompleting && authError && (
          <Alert color="red" variant="light" radius="md" title="Intake authorization required" mx="md" mt="xs">
            {authError}
          </Alert>
        )}
        {!isCompleting && intakeSubmissionBlocked && (
          <Alert color="blue" variant="light" radius="md" title="Intake temporarily unavailable" mx="md" mt="xs">
            {INTAKE_SUBMISSION_DISABLED_MESSAGE}
          </Alert>
        )}
        {!isCompleting && highRiskBookingConflict && (
          <Alert color="red" variant="light" radius="md" title="That time was just taken" mx="md" mt="xs">
            Someone else booked that time. Click Previous to choose a new time, then continue to
            confirm your booking again.
          </Alert>
        )}

        <Box className="intake-flow-main">
          {showingLoading ? (
            <Box className="intake-loading-screen">
              <Loader size="lg" color="gold" />
              {loadingMessageVisible ? (
                <Text key={loadingMessageIndex} className="intake-loading-message" fw={500} c="dark.6">
                  {loadingMessages[loadingMessageIndex]}
                </Text>
              ) : (
                <Box className="intake-loading-message-placeholder" aria-hidden />
              )}
            </Box>
          ) : (
            <Box
              key={`${activeStep}-${currentBookingRound}`}
              className="intake-step-enter"
              style={stepEnterStyle}
            >
              {!isCompleting && !isFinalStep && (
                <Text className="intake-step-title" fw={700} mb="md">
                  {isScreeningCallPhase && activeStep === 12
                    ? (() => {
                      if (clientIsHighRisk) return 'Schedule a call with your intake coordinator';
                      const screeningLabels = answers.therapyTypes
                        .filter((t) => t === 'group' || t === 'iop')
                        .map((t) => (t === 'iop' ? 'IOP' : 'Group'));
                      return screeningLabels.length > 0
                        ? `Schedule your ${screeningLabels.join(' & ')} Intake Coordinator Call`
                        : 'Schedule a call with your intake coordinator';
                    })()
                    : activeStep === 10 && activeTherapyTypes[currentBookingRound]
                      ? `Providers who match your preference for ${therapyTypeLabel(activeTherapyTypes[currentBookingRound])}`
                      : activeStep === 10
                        ? STEP_TITLES[10]
                      : activeStep === 12
                        ? (answers.sessionType === 'consultation' ? 'Book your consultation' : 'Book your intake')
                        : STEP_TITLES[activeStep]}
                </Text>
              )}
              <Stack gap="lg">
                {renderStepContent(activeStep, state, dispatch, {
                  isCompleting,
                  redirectSecondsRemaining,
                  billingPatientId,
                  isScreeningCallStep: isScreeningCallPhase,
                  isHighRiskScreeningStep: isScreeningCallPhase && clientIsHighRisk,
                  verificationError,
                  resendLoading: stepActionLoading,
                  onResend: async () => { await handleSendVerificationCode(); },
                  onWaitlist: handleWaitlistOptIn,
                  onDecline: handleWaitlistDecline,
                  intakeServices,
                  intakeServicesLoading,
                  intakeServicesError,
                  excludedTherapyTypes,
                  completionCloseLabel,
                  organizationId:
                    intakeOrganizationId ?? intakeOrganizationIdFromLink ?? undefined,
                  onToggleBookingCancelled: (index, cancelled, booking) => {
                    // Cancelling on the overview releases that booking's referral slot + held
                    // calendar Slot (any prior round) via the ticketing bot; restoring just flips state.
                    if (cancelled && !isMock && booking.reservation) {
                      void intakeSession
                        .releaseReservationForBooking(booking.reservation)
                        .catch((e) => console.error('[Intake] release on cancel failed', e));
                    }
                    dispatch({ type: 'SET_BOOKING_CANCELLED', payload: { index, cancelled } });
                  },
                })}
              </Stack>
            </Box>
          )}
        </Box>

        {!isCompleting && (
          <Box className="intake-flow-footer">
            <Flex justify="space-between" align="center" wrap="wrap" gap="md">
              {activeStep > 0 && activeStep !== 6 && !showingLoading ? (
                <Button
                  variant="default"
                  disabled={stepActionLoading}
                  onClick={() => {
                    const lastBooking = answers.bookings[answers.bookings.length - 1];
                    const lastWasWaitlisted =
                      lastBooking?.waitlisted === true ||
                      (lastBooking != null &&
                        (answers.waitlistedTherapyTypes ?? []).includes(lastBooking.therapyType));

                    if (activeStep === 13 && clientIsHighRisk) {
                      // Back to the screening-call booking to pick a new time (e.g. after a conflict).
                      // Clear the prior time + resolved slot so the user must re-pick a fresh, valid
                      // slot (bookingDate stays so the calendar reloads that day).
                      setHighRiskBookingConflict(false);
                      dispatch({ type: 'POP_LAST_BOOKING' });
                      dispatch({ type: 'SET_BOOKING_TIME', payload: null });
                      dispatch({
                        type: 'SET_SCREENING_SLOT',
                        payload: { start: null, end: null, practitionerId: null },
                      });
                      setIsScreeningCallPhase(true);
                      dispatch({ type: 'SET_STEP', payload: 12 });
                      return;
                    }

                    if (activeStep === 13) {
                      const skippedAtProvider = answers.skippedAtProviderTherapyTypes ?? [];
                      if (skippedAtProvider.length > 0) {
                        dispatch({ type: 'RESTORE_LAST_SKIPPED_AT_PROVIDER' });
                        dispatch({ type: 'SET_STEP', payload: 10 });
                        return;
                      }
                      if (lastWasWaitlisted) {
                        dispatch({ type: 'POP_LAST_BOOKING' });
                        dispatch({ type: 'SET_STEP', payload: 10 });
                        return;
                      }
                      if (lastBooking?.selectedProviderId) {
                        dispatch({ type: 'POP_LAST_BOOKING' });
                        dispatch({ type: 'SET_STEP', payload: 12 });
                        return;
                      }
                      if (getProviderSelectionTherapyTypes(answers).length > 0) {
                        dispatch({ type: 'SET_STEP', payload: 10 });
                        return;
                      }
                      dispatch({ type: 'SET_STEP', payload: 8 });
                      return;
                    }
                    if (activeStep === 10 && currentBookingRound > 0) {
                      if (lastWasWaitlisted) {
                        dispatch({ type: 'POP_LAST_BOOKING' });
                        dispatch({ type: 'SET_STEP', payload: 10 });
                      } else {
                        dispatch({ type: 'POP_LAST_BOOKING' });
                        dispatch({ type: 'SET_STEP', payload: 12 });
                      }
                      return;
                    }
                    if (activeStep === firstRoundStep && currentBookingRound > 0) {
                      dispatch({ type: 'POP_LAST_BOOKING' });
                      dispatch({ type: 'SET_STEP', payload: 12 });
                    } else if (activeStep === firstRoundStep && currentBookingRound === 0) {
                      dispatch({ type: 'SET_STEP', payload: 8 });
                    } else if (activeStep === 8) {
                      dispatch({
                        type: 'SET_STEP',
                        payload: getPreviousLinearIntakeStep(activeStep, flowVariant, {
                          ...stepShape,
                          skipEmailVerification,
                        }),
                      });
                    } else if (activeStep === 12 && !isScreeningCallPhase && !isMock) {
                      // The referral hold is created on step 10; going back to change the session
                      // type keeps it. It is only released when stepping back to provider selection.
                      dispatch({ type: 'SET_STEP', payload: 11 });
                    } else if (activeStep === 11 && !isMock) {
                      // Release this round's hold before re-picking a provider. Pass the durable
                      // reducer reservation as the fallback so prior rounds (reached via
                      // POP_LAST_BOOKING, where the hook refs are stale) release the correct slot.
                      if (stepActionInFlightRef.current) return;
                      stepActionInFlightRef.current = true;
                      setStepActionLoading(true);
                      void intakeSession
                        .releaseCurrentReservation(answers.currentReservation ?? undefined)
                        .then(() => {
                          dispatch({ type: 'SET_CURRENT_RESERVATION', payload: null });
                          dispatch({ type: 'SET_STEP', payload: 10 });
                        })
                        .catch((err) => {
                          console.error('[Intake] release on back-nav failed', err);
                          notifications.show({
                            color: 'red',
                            title: 'Could not release your previous hold',
                            message: 'Please try going back again in a moment.',
                          });
                          // Stay on step 11 and KEEP currentReservation so a retry can release
                          // via the fallback.
                        })
                        .finally(() => {
                          stepActionInFlightRef.current = false;
                          setStepActionLoading(false);
                        });
                    } else {
                      dispatch({
                        type: 'SET_STEP',
                        payload: getPreviousLinearIntakeStep(activeStep, flowVariant, {
                          ...stepShape,
                          skipEmailVerification,
                        }),
                      });
                    }
                  }}
                >
                  Previous
                </Button>
              ) : (
                <Box style={{ flex: 1 }} />
              )}
              {showCancelIntakeButton ? (
                <Button color="red" onClick={handleClose}>
                  Cancel intake
                </Button>
              ) : !isFinalStep && !showingLoading ? (
                <Button
                  color="gold"
                  disabled={
                    !canNext ||
                    stepActionLoading ||
                    answers.existingClient === true ||
                    (!isMock && intakeSession.expired) ||
                    (!isMock && !authReady) ||
                    (activeStep === 10 &&
                      !isMock &&
                      Boolean(answers.selectedProviderId) &&
                      !intakeSession.draftReady)
                  }
                  loading={stepActionLoading}
                  onClick={() => void handleNext()}
                >
                  {activeStep === 12 && isScreeningCallPhase && !clientIsHighRisk && activeTherapyTypes.length > 0
                    ? 'Book next service'
                    : activeStep === 12 && isLastRound
                      ? 'Review details'
                      : 'Next'}
                </Button>
              ) : isFinalStep && !showingLoading ? (
                <Button
                  color="gold"
                  disabled={!canNext || (!isMock && intakeSession.expired) || (!isMock && !authReady)}
                  onClick={() => {
                    void (async () => {
                      try {
                        if (!isMock && clientIsHighRisk) {
                          await submitHighRiskBooking();
                          setIsCompleting(true);
                          return;
                        }
                        if (!isMock) {
                          if (!answers.intakeLeadPatientId) {
                            throw new Error(
                              'Intake submission requires a verified patient ID. Complete email verification first.',
                            );
                          }
                          // Live intake always promotes draft SR/EoC. The non-draft fallback calls a
                          // stub that always throws — never use it.
                          const pairs = intakeSession.draftReady
                            ? intakeSession.draftPairs
                            : await intakeSession.createDraftResources(answers);
                          await intakeSession.promoteSession(answers);
                          await submit(answers, {
                            reservationPromoted: true,
                            episodeOfCareIds: pairs.map((p) => p.episodeOfCareId),
                          });
                        }
                        setIsCompleting(true);
                      } catch (err) {
                        if (isHighRiskBookingConflict(err)) {
                          setHighRiskBookingConflict(true);
                          return;
                        }
                        console.error('[Intake] submission error:', err);
                        notifications.show({
                          color: 'red',
                          title: 'Submission failed',
                          message:
                            err instanceof Error && err.message
                              ? err.message
                              : 'Please try again or contact support.',
                        });
                      }
                    })();
                  }}
                >
                  {confirmButtonLabel}
                </Button>
              ) : null}
            </Flex>
            {activeStep === 10 &&
              !isMock &&
              Boolean(answers.selectedProviderId) &&
              !intakeSession.draftReady && (
                <Text size="xs" c="dimmed" mt={4} ta="right">
                  Preparing your booking…
                </Text>
              )}
            <Progress value={progress} size="sm" mt="sm" color="gold" />
            <Text size="xs" c="dimmed" mt={4}>
              {answeredCount} of {effectiveTotal} answered
            </Text>
          </Box>
        )}
        <Box
          className="intake-dev-toolbar"
          style={{
            padding: '6px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            borderTop: '2px solid #e94560',
          }}
        >
          <Text size="xs" fw={700} c="#e94560" mr={4}>DEV</Text>
          {Array.from({ length: TOTAL_STEPS }, (_, i) => (
            <Button
              key={i}
              size="compact-xs"
              variant={activeStep === i ? 'filled' : 'light'}
              color={activeStep === i ? 'red' : 'gray'}
              style={{ minWidth: 28, fontSize: 11 }}
              onClick={() => {
                dispatch({ type: 'RESET', payload: DEV_MOCK_ANSWERS });
                dispatch({ type: 'SET_STEP', payload: i });
                setIsLoadingProviders(false);
                setIsCompleting(false);
                setIsFadingOut(false);
              }}
              title={STEP_TITLES[i]}
            >
              {i}
            </Button>
          ))}
          <Text size="xs" c="dimmed" ml={8} style={{ opacity: 0.7 }}>
            {STEP_TITLES[activeStep]}
          </Text>
        </Box>
      </Box>
    </IntakeSessionProvider>
  );
}
